import React from "react";
import get from "lodash/get";
import { colors } from "app/theme";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import * as echarts from "echarts/core";
import { PieSeriesOption } from "echarts";
import Button from "@mui/material/Button";
import { PieChart } from "echarts/charts";
import useTitle from "react-use/lib/useTitle";
import { useMediaQuery } from "@mui/material";
import useMount from "react-use/lib/useMount";
import { SVGRenderer } from "echarts/renderers";
import Typography from "@mui/material/Typography";
import { getAppName } from "app/utils/getAppName";
import { useCMSData } from "app/hooks/useCMSData";
import { StatBox } from "app/pages/common/statBox";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { formatLocale } from "app/utils/formatLocale";
import { InfoButton } from "app/components/infobutton";
import { PageSection } from "app/pages/common/section";
import { SdgsChart } from "app/components/charts/sdgs";
import { Breadcrumbs } from "app/components/breadcrumb";
import { SDGsIcon } from "app/assets/section-icons/SDGs";
import { CanonicalUrl } from "app/components/canonicalUrl";
import { SankeyChart } from "app/components/charts/sankey";
import { GeomapChart } from "app/components/charts/geomap";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { BarLineChart } from "app/components/charts/barline";
import { FloatingButtons } from "app/components/floatingButtons";
import { ActivitiesChart } from "app/components/charts/activities";
import { PrioritiesChart } from "app/components/charts/priorities";
import { PrioritiesIcon } from "app/assets/section-icons/Priorities";
import { BarLineDataItem } from "app/components/charts/barline/data";
import { ActivitiesIcon } from "app/assets/section-icons/Activities";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { getAPIFormattedFilters } from "app/utils/getAPIFormattedFilters";
import { OrganisationsIcon } from "app/assets/section-icons/Organisations";
import { getNameFieldBasedOnLang } from "app/utils/getNameFieldBasedOnLang";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { CountriesRegionsIcon } from "app/assets/section-icons/CountriesRegions";
import { DisbursementsOverviewIcon } from "app/assets/section-icons/DisbursementsOverview";
import { pageChartTooltipCommonConfig } from "app/components/charts/tooltips/common/config";
import {
  LegendComponent,
  TooltipComponent,
  LegendComponentOption,
  TooltipComponentOption,
} from "echarts/components";

echarts.use([LegendComponent, TooltipComponent, PieChart, SVGRenderer]);

const OrganisationPageIntroBlock = () => {
  const params = useParams<{ id: string }>();
  const mobile = useMediaQuery("(max-width: 768px)");
  const tablet = useMediaQuery("(max-width: 920px)");
  const cmsData = useCMSData({ returnData: true });

  const pieContainerRef = React.useRef<HTMLDivElement>(null);

  const appliedFilters = useStoreState((state) => state.appliedFilters);
  const dataPageInfo = useStoreState(
    (state) => state.organisationPageInfo.data
  );
  const fetchPageInfo = useStoreActions(
    (actions) => actions.organisationPageInfo.fetch
  );

  const dataBarChart = useStoreState(
    (state) =>
      get(
        state.disbursementsOverviewLocation,
        "data.vizData",
        []
      ) as BarLineDataItem[]
  );
  const fetchBarChart = useStoreActions(
    (actions) => actions.disbursementsOverviewLocation.fetch
  );

  const dataSDGs = useStoreState((state) =>
    get(state.sdgsSector, "data.vizData", [])
  );

  const currentLanguage = useStoreState(
    (state) => state.selectedLanguage.value as string
  );

  const nameField = getNameFieldBasedOnLang(currentLanguage);

  const dataPieChart = React.useMemo(() => {
    if (dataBarChart.length === 0) {
      return [];
    }
    const item = dataBarChart[dataBarChart.length - 1];
    return [
      {
        name: "Exclusive ODA",
        value: item.exclusive,
        itemStyle: {
          color: colors.graph.green[500],
        },
      },
      {
        name: "Other ODA",
        value: item.other,
        itemStyle: {
          color: colors.secondary.midnightBlue,
        },
      },
    ];
  }, [dataBarChart]);

  const pieTotal = React.useMemo(() => {
    if (dataBarChart.length === 0) {
      return 0;
    }
    const item = dataBarChart[dataBarChart.length - 1];
    return item.exclusive ?? 0 + (item.other ?? 0);
  }, [dataPieChart]);

  const pieChartYear = React.useMemo(() => {
    if (dataBarChart.length === 0) {
      return "";
    }
    return dataBarChart[dataBarChart.length - 1].name;
  }, [dataBarChart]);

  React.useEffect(() => {
    const filters = getAPIFormattedFilters({
      ...appliedFilters,
      organisations: [params.id ?? ""],
    });
    fetchPageInfo({
      values: {
        id: params.id,
        filters,
      },
    });
    fetchBarChart({
      values: {
        filters,
        type: "oda",
      },
    });
  }, [params.id, appliedFilters]);

  React.useEffect(() => {
    if (pieContainerRef.current) {
      const chart = echarts.init(pieContainerRef.current, undefined, {
        renderer: "svg",
      });

      const option: echarts.ComposeOption<
        PieSeriesOption | TooltipComponentOption | LegendComponentOption
      > = {
        series: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          type: "pie",
          radius: "70%",
          data: dataPieChart,
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: false,
            },
          },
        },
        legend: {
          bottom: 0,
          itemGap: 40,
          itemWidth: 14,
          icon: "circle",
          orient: "horizontal",
          textStyle: {
            fontSize: 14,
            fontFamily: "Finlandica",
            color: colors.primary.blue,
          },
        },
        tooltip: {
          ...pageChartTooltipCommonConfig(mobile),
          valueFormatter: (value) => {
            return formatLocale(value as number);
          },
        },
      };

      chart.setOption(option);
    }
  }, [pieContainerRef.current, dataPieChart]);

  const visualisations = React.useMemo(() => {
    const pieChart = (
      <Box
        sx={{
          width: "100%",
          height: "276px",
          padding: "20px",
          borderRadius: "20px",
          boxShadow: colors.shadows.inner,
          background: colors.primary.white,
        }}
      >
        <Box
          sx={{
            gap: "5px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2">
            Disbursements for {pieChartYear}
          </Typography>
          <InfoButton
            fontSize="small"
            text={get(cmsData, "tooltips.disbursements_overview", "")}
          />
        </Box>
        <Typography variant="subtitle1">{formatLocale(pieTotal)}</Typography>
        <Box
          ref={pieContainerRef}
          sx={{
            width: "100%",
            height: "200px",
          }}
        />
      </Box>
    );
    const stats = [
      <Grid item xs={12} sm={12} md={12} lg={6} key="number-of-activities">
        <StatBox
          icon={<ActivitiesIcon />}
          label="Number of activities"
          value={get(dataPageInfo, "data.activityCount", 0)}
          info={get(cmsData, "tooltips.org_page_number_of_activities", "")}
          link={`/${currentLanguage}/activities?participating-org.ref=${params.id}`}
          percentage={get(
            dataPageInfo,
            "data.activityCountChangePercentage",
            0
          )}
        />
      </Grid>,
      <Grid item xs={12} sm={12} md={12} lg={6} key="number-of-countries">
        <StatBox
          label="Number of countries"
          icon={<CountriesRegionsIcon />}
          value={get(dataPageInfo, "data.countryCount", 0)}
          info={get(cmsData, "tooltips.org_page_number_of_countries", "")}
          percentage={get(dataPageInfo, "data.countryCountChangePercentage", 0)}
        />
      </Grid>,
    ];
    const sdgsChart = (
      <Box
        sx={{
          gap: "15px",
          width: "100%",
          display: "flex",
          padding: "20px",
          flexDirection: "row",
          borderRadius: "20px",
          boxShadow: colors.shadows.inner,
          background: colors.primary.white,
        }}
      >
        <Box>
          <SDGsIcon />
        </Box>
        <Box
          sx={{
            width: "calc(100% - 67px)",
          }}
        >
          <Box
            sx={{
              gap: "10px",
              display: "flex",
              marginBottom: "5px",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">SDGs</Typography>
            <InfoButton
              fontSize="small"
              text={get(cmsData, "tooltips.sdg", "")}
            />
          </Box>
          <SdgsChart minimal items={dataSDGs} />
        </Box>
      </Box>
    );

    if (!mobile && !tablet) {
      return (
        <React.Fragment>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            {pieChart}
          </Grid>
          <Grid item container xs={6} sm={12} md={6} lg={8} spacing={2}>
            {stats}
            <Grid item xs={12}>
              {sdgsChart}
            </Grid>
          </Grid>
        </React.Fragment>
      );
    } else if (mobile) {
      return (
        <React.Fragment>
          <Grid item xs={12}>
            {pieChart}
          </Grid>
          <Grid item xs={12}>
            {stats}
          </Grid>
          <Grid item xs={12}>
            {sdgsChart}
          </Grid>
        </React.Fragment>
      );
    } else if (tablet) {
      return (
        <React.Fragment>
          <Grid item container xs={12}>
            <Grid item sm={5}>
              {pieChart}
            </Grid>
            <Grid item container sm={7} spacing={2}>
              {stats}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {sdgsChart}
          </Grid>
        </React.Fragment>
      );
    }
  }, [dataPageInfo, dataBarChart, dataSDGs, pieTotal, mobile, tablet]);

  return (
    <Box
      sx={{
        width: "100%",
        padding: "20px",
        borderRadius: "20px",
        marginBottom: "50px",
        boxShadow: colors.shadows.inner,
        background: colors.primary.gray,
      }}
    >
      <Grid container spacing={2}>
        {visualisations}
        <Grid item xs={12}>
          <Box
            sx={{
              width: "100%",
              padding: "20px",
              borderRadius: "20px",
              position: "relative",
              boxShadow: colors.shadows.inner,
              background: colors.primary.white,
            }}
          >
            <Box
              sx={{
                gap: "5px",
                width: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2">
                Disbursements Overview
              </Typography>
              <InfoButton
                fontSize="small"
                text={get(cmsData, "tooltips.disbursements_overview", "")}
              />
            </Box>
            <Typography variant="body2">
              Disbursements overview bar graph showing disbursed amount
              throughout years to{" "}
              {get(dataPageInfo, `data.names["${nameField}"]`, params.id)}.
            </Typography>
            <Box
              sx={{
                "> div": {
                  height: "250px",
                },
              }}
            >
              <BarLineChart
                detailPage
                dataVariant="oda"
                data={dataBarChart}
                disableDataZoomFeature={!mobile && !tablet}
                barWidth={!mobile ? (!tablet ? 60 : 25) : 15}
              />
            </Box>
            <Box
              sx={{
                gap: "20px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                "> div": {
                  gap: "5px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                },
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: colors.graph.green[500],
                  }}
                />
                <Typography variant="body2">Exclusive ODA</Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: colors.primary.blue,
                  }}
                />
                <Typography variant="body2">Other ODA</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              padding: "20px",
              borderRadius: "20px",
              boxShadow: colors.shadows.inner,
              background: colors.primary.white,
            }}
          >
            <Typography variant="subtitle1">News</Typography>
            <Box
              sx={{
                width: "100%",
                maxHeight: "191px",
                overflowY: "scroll",
                "> a": {
                  marginBottom: "10px",
                  color: colors.primary.blue,
                },
              }}
            >
              {get(dataPageInfo, "data.news", []).map((item: any) => (
                <a
                  target="_blank"
                  key={item.title}
                  href={item.link}
                  rel="noreferrer noopener"
                >
                  {item.title}
                </a>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              padding: "20px",
              borderRadius: "20px",
              boxShadow: colors.shadows.inner,
              background: colors.primary.white,
            }}
          >
            <Typography variant="subtitle1">Results Stories</Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export function OrganisationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const cmsData = useCMSData({ returnData: true });
  const tablet = useMediaQuery("(max-width: 920px)");
  const mobile = useMediaQuery("(max-width: 768px)");

  const appliedFilters = useStoreState((state) => state.appliedFilters);
  const dataPageInfo = useStoreState(
    (state) => state.organisationPageInfo.data
  );

  const currentLanguage = useStoreState(
    (state) => state.selectedLanguage.value as string
  );

  const nameField = getNameFieldBasedOnLang(currentLanguage);

  useTitle(
    `${get(
      dataPageInfo,
      `data.names["${nameField}"]`,
      params.id
    )} | ${getAppName(currentLanguage)}`
  );

  const fetchDisbursementsOverviewSankey = useStoreActions(
    (actions) => actions.disbursementsOverviewSankeySector.fetch
  );
  const dataDisbursementsOverviewSankey = useStoreState((state) => {
    const data: {
      nodes: {
        name: string;
        name_fi: string;
        name_se: string;
        value: number;
        percentage: number;
      }[];
      links: {
        source: string;
        target: string;
        value: number;
        percentage: number;
      }[];
    } = get(state.disbursementsOverviewSankeySector, "data.vizData", {
      nodes: [],
      links: [],
    });
    if (data.nodes.length > 0) {
      data.nodes[0].name = get(dataPageInfo, "data.names.name", "");
      data.nodes[0].name_fi = get(dataPageInfo, "data.names.name_fi", "");
      data.nodes[0].name_se = get(dataPageInfo, "data.names.name_se", "");

      data.links
        .filter((link: any) => {
          return link.source === "Total";
        })
        .forEach((link: any) => {
          link.source = get(dataPageInfo, "data.names.name", "");
        });
    }

    return data;
  });
  const loadingDisbursementsOverviewSankey = useStoreState(
    (state) => state.disbursementsOverviewSankeySector.loading
  );
  const fetchPrioritiesChart = useStoreActions(
    (actions) => actions.prioritiesChart.fetch
  );
  const dataPrioritiesChart = useStoreState((state) =>
    get(state.prioritiesChart, "data.vizData", [])
  );
  const loadingPrioritiesChart = useStoreState(
    (state) => state.prioritiesChart.loading
  );
  const fetchGeoChart = useStoreActions(
    (actions) => actions.geoChartSector.fetch
  );
  const dataGeoChart = useStoreState((state) => {
    if (!state.geoChart.data) {
      return {
        vizData: [],
        total: 0,
        totalDisbursements: 0,
        unallocablePercentage: 0,
      };
    }
    return {
      vizData: get(state.geoChartSector, "data.vizData", []),
      total: get(state.geoChartSector, "data.total", 0),
      totalDisbursements: get(
        state.geoChartSector,
        "data.totalDisbursements",
        0
      ),
      unallocablePercentage: get(
        state.geoChartSector,
        "data.unallocablePercentage",
        0
      ),
    };
  });
  const loadingGeoChart = useStoreState(
    (state) => state.geoChartSector.loading
  );
  const fetchSDGs = useStoreActions((actions) => actions.sdgsSector.fetch);
  const dataSDGs = useStoreState((state) =>
    get(state.sdgsSector, "data.vizData", [])
  );
  const loadingSDGs = useStoreState((state) => state.sdgsSector.loading);
  const fetchTop10Activities = useStoreActions(
    (actions) => actions.top10activitiesSector.fetch
  );
  const dataTop10Activities = useStoreState((state) =>
    get(state.top10activitiesSector, "data.vizData", [])
  );
  const loadingTop10Activities = useStoreState(
    (state) => state.top10activitiesSector.loading
  );
  const fetchTotalValue = useStoreActions(
    (actions) => actions.totalValue.fetch
  );

  useMount(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  });

  React.useEffect(() => {
    const filters = getAPIFormattedFilters({
      ...appliedFilters,
      organisations: [params.id ?? ""],
    });
    fetchTotalValue({
      values: {
        filters,
      },
    });
    fetchDisbursementsOverviewSankey({
      values: {
        filters,
        type: "budgetlines-country",
      },
    });
    fetchPrioritiesChart({
      values: {
        filters,
      },
    });
    fetchGeoChart({
      values: {
        filters,
      },
    });
    fetchSDGs({
      values: {
        filters,
      },
    });
    fetchTop10Activities({
      values: {
        filters,
      },
    });
  }, [params.id, appliedFilters]);

  const header = React.useMemo(() => {
    const breadcrumbs = (
      <Breadcrumbs
        route={[
          {
            label: "Homepage",
            path: "/:lang",
            cmsKey: "breadcrumbs.homepage",
          },
          {
            label: "Organisations",
            path: "/:lang/organisations",
            cmsKey: "breadcrumbs.organisation",
          },
          {
            label: get(dataPageInfo, `data.names["${nameField}"]`, ""),
          },
        ]}
      />
    );

    const title = (
      <Box
        sx={{
          gap: "15px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          "@media (max-width: 920px)": {
            marginBottom: "20px",
          },
        }}
      >
        <Box>
          <OrganisationsIcon />
        </Box>
        <Box>
          <Box
            sx={{
              gap: "5px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">
              {get(dataPageInfo, `data.names["${nameField}"]`, "")}
            </Typography>
            <InfoButton
              text={get(cmsData, "tooltips.organisation_name", "")}
              fontSize="small"
              sx={{
                color: colors.secondary.grayDark,
                "@media (max-width: 920px)": {
                  display: "none",
                },
              }}
            />
          </Box>
          <Typography component="div">
            Showcasing {get(dataPageInfo, `data.names["${nameField}"]`, "")},
            sector of Finnish ODA.
          </Typography>
        </Box>
      </Box>
    );

    const toolbar = (
      <Box
        sx={{
          gap: "10px",
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          "@media (max-width: 768px)": {
            width: "100%",
            justifyContent: "space-between",
          },
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBack fontSize="small" />}
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            padding: "4px 8px",
            textTransform: "none",
          }}
          onClick={() => {
            navigate(-1);
          }}
        >
          {get(cmsData, "general.back", "Back")}
        </Button>
        {!mobile ? (
          <FloatingButtons noFloating />
        ) : (
          <Box>
            <FloatingButtons noFloating />
          </Box>
        )}
      </Box>
    );

    const spacer = <Box sx={{ height: "20px" }} />;

    if (!tablet && !mobile) {
      return (
        <React.Fragment>
          {breadcrumbs}
          <Box
            sx={{
              display: "flex",
              marginTop: "20px",
              flexDirection: "row",
              marginBottom: "50px",
              justifyContent: "space-between",
            }}
          >
            {title}
            {toolbar}
          </Box>
        </React.Fragment>
      );
    } else if (mobile) {
      return (
        <React.Fragment>
          {breadcrumbs}
          {spacer}
          {toolbar}
          {spacer}
          {title}
        </React.Fragment>
      );
    } else if (tablet) {
      return (
        <React.Fragment>
          <Box
            sx={{
              display: "flex",
              marginTop: "20px",
              flexDirection: "row",
              marginBottom: "20px",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {breadcrumbs}
            {toolbar}
          </Box>
          {title}
        </React.Fragment>
      );
    }
  }, [tablet, mobile, cmsData, params.id, dataPageInfo, nameField]);

  return (
    <React.Fragment>
      <CanonicalUrl canonicalUrl={`/organisations/${params.id}`} />
      <Box
        sx={{
          "@media (max-width: 920px)": {
            padding: "0 48px",
          },
          "@media (max-width: 768px)": {
            padding: "0 24px",
          },
        }}
      >
        {header}
        <OrganisationPageIntroBlock />
        <PageSection
          background="#F6F9FD"
          loading={loadingDisbursementsOverviewSankey}
          header={{
            title: get(
              cmsData,
              "pageSections.OrgPageDisbursementsOverviewTitle",
              "Disbursements Overview"
            ),
            subtitle: get(
              cmsData,
              "pageSections.OrgPageDisbursementsOverviewSubtitle",
              "Disbursements overview of this organisation to its cooperation modalities and countries."
            ),
            info: get(cmsData, "tooltips.disbursements_overview", ""),
            icon: <DisbursementsOverviewIcon />,
          }}
        >
          <SankeyChart
            {...dataDisbursementsOverviewSankey}
            levelTitles={[
              get(cmsData, "viz.reportingorg", "Organisation"),
              get(cmsData, "general.budgetlines", "Budget Lines"),
              get(cmsData, "general.countries", "Countries"),
            ]}
          />
        </PageSection>
        <PageSection
          background="#FFFAF9"
          loading={loadingPrioritiesChart}
          header={{
            title: get(cmsData, "pageSections.PrioritiesTitle", "Priorities"),
            subtitle: get(
              cmsData,
              "pageSections.PrioritiesSubtitle",
              "Prioritised areas of Finnish ODA."
            ),
            info: get(cmsData, "tooltips.thematic_areas", ""),
            icon: <PrioritiesIcon />,
          }}
          sx={{ overflow: "visible" }}
        >
          <PrioritiesChart items={dataPrioritiesChart} />
        </PageSection>
        <PageSection
          background="#F9FBFA"
          loading={loadingGeoChart}
          header={{
            title: get(
              cmsData,
              "pageSections.CountriesRegionsTitle",
              "Countries & Regions"
            ),
            subtitle: get(
              cmsData,
              "pageSections.CountriesRegionsSubtitle",
              "Partner countries of Finnish ODA."
            ),
            info: get(cmsData, "tooltips.regions", ""),
            icon: <CountriesRegionsIcon />,
          }}
        >
          <GeomapChart
            data={dataGeoChart.vizData}
            unallocable={dataGeoChart.unallocablePercentage}
          />
        </PageSection>
        <PageSection
          background="#F9FCFE"
          loading={loadingSDGs}
          header={{
            title: get(cmsData, "pageSections.SDGsTitle", "SDGs"),
            subtitle: get(
              cmsData,
              "pageSections.SDGsSubtitle",
              "SDGs listed from descending order for Finnish ODA."
            ),
            info: get(cmsData, "tooltips.sdg", ""),
            icon: <SDGsIcon />,
          }}
        >
          <SdgsChart items={dataSDGs} />
        </PageSection>
        <PageSection
          background="#FCFBF9"
          loading={loadingTop10Activities}
          header={{
            title: get(cmsData, "pageSections.ActivitiesTitle", "Activities"),
            subtitle: get(
              cmsData,
              "pageSections.ActivitiesSubtitle",
              "Top 10 largest activities listed from descending order for Finnish ODA."
            ),
            info: get(cmsData, "tooltips.activities", ""),
            icon: <ActivitiesIcon />,
          }}
        >
          <Link
            id="see-all-activities-comp"
            to={`/${currentLanguage}/activities${
              location.search.length > 0 ? `${location.search}&` : "?"
            }participating-org.ref=${params.id}`}
          >
            {get(cmsData, "viz.seeallactivities", "See All Activities")}{" "}
            <ArrowForward fontSize="small" />
          </Link>
          <ActivitiesChart items={dataTop10Activities} />
        </PageSection>
      </Box>
    </React.Fragment>
  );
}
